import { tidyCites } from "@typedoc/contracts/chat";
import type { Atom } from "./atoms";
import { parseHeld } from "./engine";

function piece(atom: Atom): string | null {
  switch (atom.kind) {
    case "chip":
      // A cite's label is a key, not a word: it says nothing where there is no chip to open.
      return atom.name === "cite" ? null : atom.text;
    case "word":
    case "code":
    case "line":
      return atom.text;
    case "cite":
      return `[${atom.nums.join(", ")}]`;
    case "row":
      return atom.cells.map((cell) => joined(cell.items)).join(" · ");
    case "open":
    case "hr":
      return null;
  }
}

function joined(atoms: readonly Atom[]): string {
  let out = "";
  let previous = "";
  for (const atom of atoms) {
    const text = piece(atom);
    if (text === null) continue;
    const block = atom.path.findLast((frame) => frame.kind === "block")?.id ?? "";
    // A new block starts a new piece of text; inside one, a space only where the source had one.
    // A line of code and a row of a table are records of their own, though they share a block.
    const record = atom.kind === "line" || atom.kind === "row";
    if (out !== "" && (atom.lead || record || block !== previous)) out += " ";
    out += text;
    previous = block;
  }
  return out;
}

/**
 * What an answer says, without its marks: the words, a directive as its label, a table row as its
 * cells. For the places that show a line of a message and have no room for the drawing of it (the
 * list's peek, a search hit), so they never show `:process[...]{id=...}`.
 */
export const plainText = (source: string): string => joined(parseHeld(tidyCites(source), true));

/** A run of text and the inline marks it keeps: what `inlineRuns` gives `InlineMarkdown`. */
export type InlineRun = {
  readonly text: string;
  readonly strong?: true;
  readonly em?: true;
  readonly code?: true;
};

type Marks = Omit<InlineRun, "text">;

function marksOf(atom: Atom): Marks {
  if (atom.kind === "code") return { code: true };
  const tags = new Set(atom.path.flatMap((frame) => (frame.kind === "inline" ? [frame.tag] : [])));
  return {
    ...(tags.has("strong") ? { strong: true as const } : {}),
    ...(tags.has("em") ? { em: true as const } : {}),
  };
}

const sameMarks = (a: Marks, b: Marks) =>
  a.strong === b.strong && a.em === b.em && a.code === b.code;

/**
 * A preview cut mid-sentence can leave a mark open ("It covers **reviewing the"): the last `**` or
 * backtick with no partner is dropped, so the tail reads as plain words, not as asterisks.
 */
function closeCut(source: string): string {
  let text = source;
  // A backtick with no partner first, so what is left pairs into whole code spans.
  if (text.split("`").length % 2 === 0) {
    const at = text.lastIndexOf("`");
    text = text.slice(0, at) + text.slice(at + 1);
  }
  // Then a `**` with no partner, counting only those outside code: `2 ** 3` keeps its operator.
  const outside: number[] = [];
  let inCode = false;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === "`") inCode = !inCode;
    else if (!inCode && text.startsWith("**", i)) {
      outside.push(i);
      i += 1;
    }
  }
  const last = outside.at(-1);
  if (outside.length % 2 === 1 && last !== undefined)
    text = text.slice(0, last) + text.slice(last + 2);
  return text;
}

/**
 * What a message says as one line of text that keeps its inline marks: bold, italic and code stay,
 * everything else reads as `plainText` does (a link is its words, a directive its label, blocks run
 * together). For the places that show a message in a row or a card, where a heading or a list
 * would not fit but `**` should never show.
 */
export function inlineRuns(source: string): InlineRun[] {
  const runs: InlineRun[] = [];
  const push = (text: string, marks: Marks) => {
    const last = runs.at(-1);
    if (last && sameMarks(last, marks)) runs[runs.length - 1] = { ...last, text: last.text + text };
    else runs.push({ text, ...marks });
  };
  let previousBlock = "";
  let previousMarks: Marks | null = null;
  for (const atom of parseHeld(tidyCites(closeCut(source)), true)) {
    const text = piece(atom);
    if (text === null) continue;
    const marks = marksOf(atom);
    const block = atom.path.findLast((frame) => frame.kind === "block")?.id ?? "";
    const record = atom.kind === "line" || atom.kind === "row";
    if (previousMarks !== null && (atom.lead || record || block !== previousBlock)) {
      // A space between two runs of the same marks belongs to them; between different ones, to neither.
      push(" ", sameMarks(previousMarks, marks) ? marks : {});
    }
    push(text, marks);
    previousBlock = block;
    previousMarks = marks;
  }
  return runs;
}
